import type {
  PasswordFieldOptions,
  PasswordFieldProps,
  PasswordFieldRule,
} from "./password-field.type";

import { LockOutlined } from "@ant-design/icons";
import { Form, Input } from "antd";
import { useTranslation } from "react-i18next";

import { PASSWORD_FIELD_NAME } from "@/modules/password-field/password-field.constant";

export const PasswordField = ({
  label: customLabel,
  enforceStrongPassword = false,
  ...formItemProps
}: PasswordFieldProps & PasswordFieldOptions) => {
  const { t } = useTranslation();
  const label = customLabel ?? t("auth:form.password.label");

  const lowercaseLetterRegex = new RegExp(/[a-z]/);
  const uppercaseLetterRegex = new RegExp(/[A-Z]/);
  const numberRegex = new RegExp(/[0-9]/);
  // From aws cognito docs:
  // https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
  const specialCharacterRegex = new RegExp(
    /[!@#$%^?&*().,><':;_~`"=+}{\-\][\\/|]/
  );

  const rules: PasswordFieldRule[] = [
    { required: true, message: t("auth:form.password.errors.empty") },
  ];

  const strongPasswordRules: PasswordFieldRule[] = [
    { required: true, message: t("auth:form.newPassword.errors.empty") },
    { min: 8, message: t("auth:form.newPassword.errors.min") },
    {
      pattern: lowercaseLetterRegex,
      message: t("auth:form.newPassword.errors.lowercaseLetter"),
    },
    {
      pattern: uppercaseLetterRegex,
      message: t("auth:form.newPassword.errors.uppercaseLetter"),
    },
    { pattern: numberRegex, message: t("auth:form.newPassword.errors.number") },
    {
      pattern: specialCharacterRegex,
      message: t("auth:form.newPassword.errors.specialCharacter"),
    },
  ];
  return (
    <Form.Item
      name={PASSWORD_FIELD_NAME}
      label={label}
      required
      rules={enforceStrongPassword ? strongPasswordRules : rules}
      {...formItemProps}
    >
      <Input.Password
        placeholder={label}
        prefix={<LockOutlined className="inputIcon" />}
      />
    </Form.Item>
  );
};
